import { CircularProgress } from '@mui/material';
import React, { useRef } from 'react';

import ExceptionalPartCreateStep from './ExceptionalPartCreateStep';
import { ExceptionalPartCreateStepRef } from './ExceptionalPartCreateStep.props';
import { MobilePartActionsDialogProps } from './MobilePartActionsDialog.props';
import { LoadingContainer } from './MobilePartActionsDialog.styles';
import { PartActionConfirmStep } from './PartActionConfirmStep';
import { PartActionInteractionStep } from './PartActionInteractionStep';
import { PartActionSelectionStep } from './PartActionSelectionStep';
import { PartActionsFooter } from './PartActionsFooter';
import { PartTypeSelectionStep } from './PartTypeSelectionStep';
import { useMobilePartActions } from './useMobilePartActions';
import { BottomSheet } from '../../../../../../components/molecules/BottomSheet';

export const MobilePartActionsDialog: React.FC<MobilePartActionsDialogProps> = ({
  open,
  onClose,
  service,
  partActionTypes,
  onPartActionComplete,
}) => {
  const exceptionalStepRef = useRef<ExceptionalPartCreateStepRef>(null);
  const {
    step,
    setStep,
    selectedAction,
    handleActionSelect,
    notes,
    setNotes,
    error,
    interactions,
    formValues,
    isBlocked,
    exceptionalFor,
    exceptionalInitialSerial,
    canSubmitExceptional,
    setCanSubmitExceptional,
    search,
    addBulkPartActionsMutation,
    createExceptionalPartMutation,
    handleSubmit,
    handlePartTypeSelect,
    handleInteractionSubmit,
    handleExceptionalSubmit,
    handleFieldChange,
    handleBlockBypass,
    resetState,
    handleClose,
    canSubmit,
    getTitle,
    onCreateOutExceptionally,
    onCreateInExceptionally,
  } = useMobilePartActions(service, onClose, onPartActionComplete);

  const isExceptionalStep = step === 'exceptional-create';
  const isConfirmStep = step === 'confirm';
  const isInteractionStep = step === 'interactions';
  const isPartTypeStep = step === 'select-part-type';
  const showFooter = isConfirmStep || isInteractionStep || isExceptionalStep || isPartTypeStep;

  const renderStepContent = () => {
    if (step === 'select-action') {
      return (
        <PartActionSelectionStep
          partActionTypes={partActionTypes || []}
          onSelectAction={handleActionSelect}
        />
      );
    }
    if (isPartTypeStep && selectedAction) {
      return (
        <PartTypeSelectionStep
          defaultResults={search.partTypeDefaults}
          isSearching={search.isSearching}
          onSelect={handlePartTypeSelect}
        />
      );
    }
    if (isConfirmStep && selectedAction) {
      return (
        <PartActionConfirmStep
          selectedAction={selectedAction}
          error={error}
          outSerial={search.outSerial}
          inSerial={search.inSerial}
          selectedPartType={search.selectedPartType}
          notes={notes}
          onNotesChange={setNotes}
          onOutSelect={(item) => item && search.setOutSerial(search.makeSerial(item))}
          onInSelect={(item) => item && search.setInSerial(search.makeSerial(item))}
          onOutClear={() => search.setOutSerial(null)}
          onInClear={() => search.setInSerial(null)}
          onOutSearch={(val) => search.handleSearch(val, true)}
          onInSearch={(val) => search.handleSearch(val, false)}
          isSearching={search.isSearching}
          onCreateOutExceptionally={onCreateOutExceptionally}
          onCreateInExceptionally={onCreateInExceptionally}
          onPartTypeClick={search.selectedPartType ? () => {
            search.setSelectedPartType(null);
            setStep('select-part-type');
          } : undefined}
          onActionClick={resetState}
        />
      );
    }
    if (isExceptionalStep && selectedAction && exceptionalFor) {
      return (
        <ExceptionalPartCreateStep
          ref={exceptionalStepRef}
          actionFor={exceptionalFor}
          service={service}
          isSubmitting={createExceptionalPartMutation.isPending}
          error={error}
          initialSerial={exceptionalInitialSerial}
          onSubmit={handleExceptionalSubmit}
          onValidChange={setCanSubmitExceptional}
        />
      );
    }
    if (isInteractionStep && selectedAction) {
      return (
        <PartActionInteractionStep
          selectedAction={selectedAction}
          notes={notes}
          onNotesChange={setNotes}
          error={error}
          interactions={interactions}
          formValues={formValues}
          onFieldChange={handleFieldChange}
          onBlockBypass={handleBlockBypass}
          isBlocked={isBlocked}
          service={service}
        />
      );
    }
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    );
  };

  return (
    <BottomSheet
      open={open}
      onClose={handleClose}
      height={isConfirmStep || isInteractionStep || isExceptionalStep || isPartTypeStep ? 'large' : 'medium'}
      title={getTitle()}
      subtitle={step === 'select-action' ? 'Select an action to record' : undefined}
      footer={
        showFooter ? (
          <PartActionsFooter
            isPartTypeStep={isPartTypeStep}
            isExceptionalStep={isExceptionalStep}
            isConfirmStep={isConfirmStep}
            isInteractionStep={isInteractionStep}
            canSubmit={canSubmit()}
            isBlocked={isBlocked}
            canSubmitExceptional={canSubmitExceptional}
            isCreating={createExceptionalPartMutation.isPending}
            isSubmitting={addBulkPartActionsMutation.isPending}
            onBack={() => setStep('confirm')}
            onCancel={resetState}
            onInteractionSubmit={handleInteractionSubmit}
            onExceptionalSubmit={() => exceptionalStepRef.current?.submit()}
            onConfirmSubmit={handleSubmit}
          />
        ) : undefined
      }
    >
      {renderStepContent()}
    </BottomSheet>
  );
};
